import { useStore } from '@nanostores/preact'
import { FORM_ERROR, FormState } from 'final-form'
import { useMemo, useRef, useState } from 'preact/hooks'
import { Field, Form, FormRenderProps, FormSpy } from 'react-final-form'

import classNames from 'utils/preact/class-names'

import { Sidebar } from '../components/Sidebar'
import { SubscriptionButtons } from '../components/SubscriptionButtons'
import { useTranslation } from '../components/Translation'
import { FocusFirstError } from '../services/focus-first-error'
import { send } from '../services/backend'
import { checkEmail, checkPassword, checkPasswordConfirm } from '../services/validation'
import { profile, SubscriptionValue } from '../services/store'
import { IsoCountry } from '../services/support'
import { Screens } from '../services/screens'

import eyeSlash from '../static/eye-slash.svg'
import eye from '../static/eye.svg'
import css from '../style.module.scss'

interface FormValues {
  email: string
  isoCountry: string
  password: string
  passwordConfirm: string
  subscription: SubscriptionValue
}

export function SignUp({
  token,
  isoCountries,
  onAuthenticate,
  onPageUpdate
}: {
  token: string
  isoCountries: IsoCountry[]
  onAuthenticate: () => void
  onPageUpdate: (page: Screens) => void
}) {
  const t = useTranslation()
  const user = useStore(profile)
  const formRef = useRef<HTMLFormElement>(null)
  const decorator = useMemo(() => FocusFirstError(formRef), [])
  const [isInitialized, setIsInitialized] = useState(false)
  const [passwordHidden, setPasswordHidden] = useState(true)
  // const { currencies, products } = support.get()
  // console.log(currencies, products)

  return (
    <div className={css.content}>
      <div className={css['split-container']}>
        <div className={css['flex-grow']}>
          <Sidebar />
          <SubscriptionButtons />
        </div>
        <div className={css['horizontal-divider']} />
        <div className={css['flex-grow']}>
          <h3 className={css['form-title']}>{t('your_account')}</h3>
          <p className={css['account-text']}>{t('account_for_app')}</p>

          <p
            className={classNames({
              [css['text-regular']]: true,
              [css['font-bold']]: true
            })}
          >
            {t('already_a_member')}{' '}
            <a
              className={classNames({
                [css['text-highlight']]: true,
                [css['text-underline']]: true
              })}
              href="/sign_in"
              onClick={(e) => {
                e.preventDefault()
                onPageUpdate(Screens.SignInScreen)
              }}
            >
              {t('log_in')}
            </a>
          </p>

          <Form
            initialValues={{
              isoCountry: user.countryIso
            }}
            decorators={[decorator]}
            onSubmit={(values: FormValues): Promise<Record<string, string> | undefined> => {
              let url: string
              let method: string

              if (user.newAccountCreated) {
                url = '/user'
                method = 'put'
              } else {
                url = '/users/create_with_confirmation'
                method = 'post'
              }

              return send<FormValues & { source: string }>({
                url,
                method,
                body: JSON.stringify({ user: { ...values, source: user.source } }),
                token
              }).then((result) => {
                if (result.success) return undefined
                if (result.error) return { [FORM_ERROR]: result.error }
                return result.errors
              })
            }}
          >
            {({
              handleSubmit,
              pristine,
              submitting,
              submitError,
              hasSubmitErrors,
              hasValidationErrors,
              values,
              form
            }: FormRenderProps & FormState<FormValues>) => (
              <form action="/users" method="post" onSubmit={handleSubmit} ref={formRef}>
                <FormSpy
                  subscription={{ pristine: true }}
                  onChange={() => {
                    if (!isInitialized) {
                      form.batch(() => {
                        form.change('email', user.email)
                        form.change('password', user.password)
                        form.change('subscription', user.subscription)
                        form.change('passwordConfirm', user.passwordConfirm)
                        form.change('isoCountry', user.countryIso)
                      })
                      setIsInitialized(true)
                    }
                  }}
                />

                <FormSpy
                  subscription={{ submitSucceeded: true }}
                  onChange={({ submitSucceeded }: { submitSucceeded: boolean }) => {
                    if (submitSucceeded) {
                      profile.setKey('newAccountCreated', true)
                      profile.setKey('reloadRequired', true)
                      profile.setKey('authorized', true)

                      profile.setKey('email', values.email)
                      profile.setKey('password', values.password)
                      profile.setKey('passwordConfirm', values.passwordConfirm)
                      profile.setKey('subscription', values.subscription)
                      profile.setKey('countryIso', values.isoCountry)

                      onAuthenticate()
                    }
                  }}
                />
                <Field
                  name="email"
                  validate={(value: string) => checkEmail(t, value)}
                  render={({ input, meta }) => (
                    <label
                      className={classNames({
                        [css.label]: true,
                        [css['is-valid']]: meta.touched && meta.valid,
                        [css['is-invalid']]: meta.touched && meta.invalid
                      })}
                      htmlFor="email"
                    >
                      {t('email')}
                      <span className={css.error}>
                        {((meta.touched && meta.error) || meta.submitError) &&
                          `(${(meta.touched && meta.error) || meta.submitError})`}
                      </span>
                      <div
                        className={classNames({
                          [css['input-wrapper']]: true,
                          [css['is-valid']]: meta.touched && meta.valid,
                          [css['is-invalid']]: meta.touched && meta.invalid
                        })}
                      >
                        <input
                          {...input}
                          id="email"
                          type="email"
                          autoComplete="email"
                          className={classNames({
                            [css.input]: true,
                            [css['is-valid']]: meta.touched && meta.valid,
                            [css['is-invalid']]: meta.touched && meta.invalid
                          })}
                        />
                      </div>
                    </label>
                  )}
                />

                <Field
                  name="isoCountry"
                  render={({ input, meta }) => (
                    <label
                      className={classNames({
                        [css.label]: true,
                        [css['is-valid']]: meta.touched && meta.valid,
                        [css['is-invalid']]: meta.touched && meta.invalid
                      })}
                      htmlFor="isoCountry"
                    >
                      {t('country')}
                      <span className={css.error}>
                        {((meta.touched && meta.error) || meta.submitError) &&
                          `(${(meta.touched && meta.error) || meta.submitError})`}
                      </span>
                      <select
                        {...input}
                        id="isoCountry"
                        className={classNames({
                          [css.input]: true,
                          [css['is-dropdown']]: true,
                          [css['is-valid']]: meta.touched && meta.valid,
                          [css['is-invalid']]: meta.touched && meta.invalid
                        })}
                      >
                        {isoCountries.map((isoCountry) => (
                          <option value={isoCountry.iso}>{isoCountry.name}</option>
                        ))}
                      </select>
                    </label>
                  )}
                />

                <Field
                  name="password"
                  validate={(value: string) => checkPassword(t, value)}
                  render={({ input, meta }) => (
                    <label
                      className={classNames({
                        [css.label]: true,
                        [css['is-valid']]: meta.touched && meta.valid,
                        [css['is-invalid']]: meta.touched && meta.invalid
                      })}
                      htmlFor="password"
                    >
                      {t('password')}
                      <span className={css.error}>
                        {((meta.touched && meta.error) || meta.submitError) &&
                          `(${(meta.touched && meta.error) || meta.submitError})`}
                      </span>
                      <div className={css['password-field-label']}>
                        <button
                          type="button"
                          className={css['password-field-toggle']}
                          onClick={() => setPasswordHidden((value) => !value)}
                        >
                          <img
                            src={passwordHidden ? eye : eyeSlash}
                            alt={passwordHidden ? 'Show password' : 'Hide password'}
                          />
                        </button>
                      </div>
                      <div
                        className={classNames({
                          [css['input-wrapper']]: true,
                          [css['is-valid']]: meta.touched && meta.valid,
                          [css['is-invalid']]: meta.touched && meta.invalid
                        })}
                      >
                        <input
                          {...input}
                          id="password"
                          type={passwordHidden ? 'password' : 'text'}
                          autoComplete="new-password"
                          className={classNames({
                            [css.input]: true,
                            [css['is-valid']]: meta.touched && meta.valid,
                            [css['is-invalid']]: meta.touched && meta.invalid
                          })}
                        />
                      </div>
                    </label>
                  )}
                />
                <Field
                  name="passwordConfirm"
                  validate={(value: string, { password }: FormValues) =>
                    checkPasswordConfirm(t, value, password)
                  }
                  render={({ input, meta }) => (
                    <label
                      className={classNames({
                        [css.label]: true,
                        [css['is-valid']]: meta.touched && meta.valid,
                        [css['is-invalid']]: meta.touched && meta.invalid
                      })}
                      htmlFor="passwordConfirm"
                    >
                      {t('repeat_password')}
                      <span className={css.error}>
                        {((meta.touched && meta.error) || meta.submitError) &&
                          `(${(meta.touched && meta.error) || meta.submitError})`}
                      </span>
                      <div
                        className={classNames({
                          [css['input-wrapper']]: true,
                          [css['is-valid']]: meta.touched && meta.valid,
                          [css['is-invalid']]: meta.touched && meta.invalid
                        })}
                      >
                        <input
                          {...input}
                          id="passwordConfirm"
                          type={passwordHidden ? 'password' : 'text'}
                          autoComplete="new-password"
                          className={classNames({
                            [css.input]: true,
                            [css['is-valid']]: meta.touched && meta.valid,
                            [css['is-invalid']]: meta.touched && meta.invalid
                          })}
                        />
                      </div>
                    </label>
                  )}
                />

                <div className={css['spacer-bottom']} />

                {hasSubmitErrors && (
                  <p
                    className={classNames({
                      [css['text-medium']]: true,
                      [css['text-center']]: true,
                      [css['error-highlight']]: true
                    })}
                  >
                    {submitError}
                  </p>
                )}
                <button
                  className={classNames({
                    [css.submit]: true,
                    [css['is-loading']]: submitting
                  })}
                  type="submit"
                  disabled={pristine || hasValidationErrors}
                >
                  {t('continue')}
                </button>
              </form>
            )}
          </Form>

          <p className={css['terms-text']}>
            {t('agree_to_terms')}{' '}
            <a href="/pages/terms" target="_blank" rel="noopener noreferrer">
              Terms & Services
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
